import { useMemo } from 'react';

import { usePageMenuNames } from 'hb-react/shared/hooks/usePageMenuNames';
import { ENTITY_PAGE_MAP } from 'hb-react/shared/utils/entitiesContentData/constants';
import { type EntityType } from 'hb-react/shared/utils/entitiesContentData/types';

import { getPageIdentifiersForEntityTypes } from '../utils/entityPages';

/**
 * Hook that maps entity types to the customizable names of the pages they are associated with.
 *
 * If entity type is not associated with a page, it will be ignored.
 *
 * @param entityTypes {Array<EntityType>} The entity types to map to page names. Defaults to all entity types.
 * @returns A map of entity type identifiers to their display names.
 * @example
 * const entityPageNames = useEntityPageNames();
 * // { 'admin_event': 'Events', 'company': 'Companies', 'news': 'News', ... }
 */
export const useEntityPageNames = <T extends Array<EntityType>>(
  entityTypes?: T,
): Partial<Record<T[number], string>> => {
  // If no entity types are provided, use all entity types.
  const entityTypesList = useMemo(
    () => entityTypes ?? (Object.keys(ENTITY_PAGE_MAP) as T),
    [entityTypes],
  );
  const requestedPageIdentifiers =
    getPageIdentifiersForEntityTypes(entityTypesList);

  // Returns a hash of page identifiers and custom page names
  const pageNames = usePageMenuNames(requestedPageIdentifiers);

  return useMemo(
    () =>
      entityTypesList.reduce((acc, entityType) => {
        const pageIdentifier = ENTITY_PAGE_MAP[entityType];
        return {
          ...acc,
          [entityType]: pageIdentifier ? pageNames[pageIdentifier] : undefined,
        };
      }, {}),
    [entityTypesList, pageNames],
  );
};
